<script setup lang="ts">
import { RefreshCcw04, Bell03, Moon01 } from "utruckin-icons";
import { useRoute } from "vue-router";
import { computed, onMounted, onUnmounted, ref } from "vue";
import WidgetProfile from "@/components/widget/WidgetProfile.vue";
import { useAppStore } from "@/store/app.store";
import { useTheme } from "@/utils/useMode";
import CustomNotificationModal from "@/components/custom/CustomNotificationModal.vue";
import { useNotificationStore } from "@/features/notification/notification.store";

const route = useRoute();
const appStore = useAppStore();
const notificationStore = useNotificationStore();
const { toggleTheme, watchSystemTheme } = useTheme();

let cleanup: (() => void) | null = null;

const notificationModal = ref<boolean>(false);

const routerName = computed(() => {
  const fullRoute = route.name as string;
  return fullRoute.split("-")[0];
});

const refresh = () => {
  appStore.triggerRefresh();
};

const openNotification = () => {
  notificationModal.value = true;
};

onMounted(() => {
  cleanup = watchSystemTheme();
});

onUnmounted(() => {
  cleanup?.();
});
</script>

<template>
  <div class="c-w-header">
    <div class="title" :class="route.params.id ? 'link' : ''">
      <div class="title__name">{{ routerName }}</div>
    </div>

    <div class="actions">
      <div class="refresh" @click="refresh">
        <RefreshCcw04 :size="24" />
      </div>

      <div class="notification" @click="openNotification">
        <Bell03 :size="24" />

        <div
          v-if="notificationStore.notificationCount"
          class="notification__count"
        >
          {{ notificationStore.notificationCount }}
        </div>
      </div>

      <div class="theme" @click="toggleTheme">
        <Moon01 :size="24" />
      </div>

      <WidgetProfile />
    </div>
  </div>

  <CustomNotificationModal v-model="notificationModal" />
</template>

<style lang="scss">
.c-w-header {
  margin-top: var(--base-space-8);
  padding: 16px;
  height: 72px;
  width: 100%;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-system-background);
  border-top-left-radius: var(--base-radius-20);
  border-top-right-radius: var(--base-radius-20);

  .title {
    display: flex;
    align-items: end;
    gap: 4px;
    color: var(--color-label);

    &.link {
      .title__name:hover {
        text-decoration: underline;
      }
    }

    &__name {
      cursor: pointer;
      text-transform: capitalize;
      font-weight: 600;
      font-size: 24px;
      line-height: 32px;
    }

    &__sub {
      font-size: 14px;
      line-height: 22px;
      color: var(--color-label-secondary);
    }
  }

  .actions {
    display: flex;
    gap: 8px;

    .refresh,
    .theme,
    .notification {
      position: relative;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--color-border);
      border-radius: 12px;
      cursor: pointer;
      color: var(--color-label);
      background: var(--color-secondary-system-background);

      path {
        stroke-width: 1.5px !important;
      }

      &:hover {
        border-color: var(--color-primary-disabled);
      }

      &__count {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        padding: 2px 4px;
        background: var(--color-system-red);
        font-size: 12px;
        line-height: 12px;
        top: 0;
        right: 0;
        color: var(--color-white);
        border-radius: 50%;
        transform: translate(30%, -30%);
      }
    }

    .search {
      width: 240px;
      height: 40px;
      border: 1px solid var(--color-border);
      background: var(--color-bg-secondary);
      border-radius: 12px;
      cursor: pointer;
      padding: 10px 12px;
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--color-label-secondary);

      &:hover {
        border-color: var(--color-primary-disabled);
      }

      span {
        background: transparent;
      }
    }
  }
}
</style>
